import { ChecklistDiscussion } from '@/types/discussion';
import { MessageItem } from './MessageItem';
import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';

interface MessageListProps {
  messages: ChecklistDiscussion[];
  currentUserId: number;
  isLoading?: boolean;
  onEdit?: (id: number, newMessage: string) => void;
  onDelete?: (id: number) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
}

export const MessageList = ({
  messages,
  currentUserId,
  isLoading,
  onEdit,
  onDelete,
  onLoadMore,
  hasMore,
  isLoadingMore,
}: MessageListProps) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const previousScrollHeightRef = useRef<number>(0);
  const previousMessagesLengthRef = useRef<number>(0);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Auto-scroll only when new messages arrive (not when loading older messages)
  useEffect(() => {
    const isNewMessage = messages.length > previousMessagesLengthRef.current && !isLoadingMore;

    if (isNewMessage) {
      scrollToBottom();
    }

    previousMessagesLengthRef.current = messages.length;
  }, [messages.length, isLoadingMore]);

  // Maintain scroll position when loading older messages
  useEffect(() => {
    if (isLoadingMore && containerRef.current) {
      previousScrollHeightRef.current = containerRef.current.scrollHeight;
    }
  }, [isLoadingMore]);

  useEffect(() => {
    if (!isLoadingMore && containerRef.current && previousScrollHeightRef.current > 0) {
      const newScrollHeight = containerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - previousScrollHeightRef.current;
      containerRef.current.scrollTop = scrollDiff;
      previousScrollHeightRef.current = 0;
    }
  }, [isLoadingMore]);

  if (isLoading && messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <p className="font-medium">Belum ada pesan</p>
        <p className="text-sm mt-1">Jadilah yang pertama untuk memulai diskusi</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full overflow-y-auto py-2 md:py-4">
      {hasMore && (
        <div className="text-center py-2">
          <button
            onClick={onLoadMore}
            className="text-sm text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                Memuat...
              </span>
            ) : (
              'Muat pesan sebelumnya'
            )}
          </button>
        </div>
      )}

      {messages.map(message => (
        <MessageItem
          key={message.id}
          message={message}
          currentUserId={currentUserId}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      ))}

      <div ref={messagesEndRef} />
    </div>
  );
};
